/**
 * PageHeader — the router-aware wrapper over the shared `@argus/ui` PageHeader
 * (packages/ui/src/page-header.tsx). Same header, same classes; the one difference is
 * `backTo`: here it is a typed route (`{ to, params, label }`) rendered as a TanStack
 * `<Link>` into the package's `backTo` slot, so screens keep the props they always had.
 */
import { PageHeader as SharedPageHeader, type PageHeaderProps as SharedProps } from '@argus/ui';
import { Link } from '@tanstack/react-router';
import { Linear } from './icons.ts';
import type { ComponentProps } from 'react';

const { ChevronLeft } = Linear;

/** A back link: any registered route path plus its params. Typed loosely because Link's
 *  route generics cannot be carried through a prop object; callers pass real route paths. */
interface BackLink {
  to: string;
  params?: Record<string, string> | undefined;
  label: string;
}
type LinkProps = ComponentProps<typeof Link>;

export interface PageHeaderProps extends Omit<SharedProps, 'backTo'> {
  /** A "‹ Back to …" link above the title. */
  backTo?: BackLink | undefined;
}

function backLinkProps(back: BackLink): Pick<LinkProps, 'to' | 'params'> {
  return { to: back.to, params: back.params } as unknown as Pick<LinkProps, 'to' | 'params'>;
}

export function PageHeader({ backTo, ...rest }: PageHeaderProps): JSX.Element {
  return (
    <SharedPageHeader
      {...rest}
      backTo={
        backTo ? (
          <Link {...backLinkProps(backTo)}>
            <ChevronLeft size={14} aria-hidden="true" />
            {backTo.label}
          </Link>
        ) : undefined
      }
    />
  );
}
